/**
 * PhoneInput — Country-code-aware phone field
 * Props:
 *   value       : { countryCode: "+91", number: "9876543210" }
 *   onChange    : (newValue) => void
 *   error       : string | undefined
 *   label       : string
 *   required    : bool
 *   placeholder : string (optional)
 */

import { useState, useRef, useEffect } from "react";

/* ── Country data: { code, flag, name, digits } ─────────────────── */
export const COUNTRIES = [
  { code: "+91",  flag: "🇮🇳", name: "India",          digits: 10 },
  { code: "+1",   flag: "🇺🇸", name: "USA / Canada",   digits: 10 },
  { code: "+44",  flag: "🇬🇧", name: "UK",             digits: 10 },
  { code: "+61",  flag: "🇦🇺", name: "Australia",      digits: 9  },
  { code: "+971", flag: "🇦🇪", name: "UAE",            digits: 9  },
  { code: "+966", flag: "🇸🇦", name: "Saudi Arabia",   digits: 9  },
  { code: "+65",  flag: "🇸🇬", name: "Singapore",      digits: 8  },
  { code: "+60",  flag: "🇲🇾", name: "Malaysia",       digits: 9  },
  { code: "+94",  flag: "🇱🇰", name: "Sri Lanka",      digits: 9  },
  { code: "+977", flag: "🇳🇵", name: "Nepal",          digits: 10 },
  { code: "+880", flag: "🇧🇩", name: "Bangladesh",     digits: 10 },
  { code: "+92",  flag: "🇵🇰", name: "Pakistan",       digits: 10 },
  { code: "+49",  flag: "🇩🇪", name: "Germany",        digits: 10 },
  { code: "+33",  flag: "🇫🇷", name: "France",         digits: 9  },
  { code: "+39",  flag: "🇮🇹", name: "Italy",          digits: 10 },
  { code: "+34",  flag: "🇪🇸", name: "Spain",          digits: 9  },
  { code: "+81",  flag: "🇯🇵", name: "Japan",          digits: 10 },
  { code: "+82",  flag: "🇰🇷", name: "South Korea",    digits: 10 },
  { code: "+86",  flag: "🇨🇳", name: "China",          digits: 11 },
  { code: "+55",  flag: "🇧🇷", name: "Brazil",         digits: 11 },
  { code: "+52",  flag: "🇲🇽", name: "Mexico",         digits: 10 },
  { code: "+27",  flag: "🇿🇦", name: "South Africa",   digits: 9  },
  { code: "+234", flag: "🇳🇬", name: "Nigeria",        digits: 10 },
  { code: "+254", flag: "🇰🇪", name: "Kenya",          digits: 9  },
  { code: "+7",   flag: "🇷🇺", name: "Russia",         digits: 10 },
  { code: "+20",  flag: "🇪🇬", name: "Egypt",          digits: 10 },
  { code: "+31",  flag: "🇳🇱", name: "Netherlands",    digits: 9  },
  { code: "+46",  flag: "🇸🇪", name: "Sweden",         digits: 9  },
  { code: "+47",  flag: "🇳🇴", name: "Norway",         digits: 8  },
  { code: "+45",  flag: "🇩🇰", name: "Denmark",        digits: 8  },
  { code: "+41",  flag: "🇨🇭", name: "Switzerland",    digits: 9  },
  { code: "+64",  flag: "🇳🇿", name: "New Zealand",    digits: 9  },
  { code: "+63",  flag: "🇵🇭", name: "Philippines",    digits: 10 },
  { code: "+66",  flag: "🇹🇭", name: "Thailand",       digits: 9  },
  { code: "+62",  flag: "🇮🇩", name: "Indonesia",      digits: 11 },
  { code: "+84",  flag: "🇻🇳", name: "Vietnam",        digits: 9  },
];

export function getCountryByCode(code) {
  return COUNTRIES.find(c => c.code === code) || COUNTRIES[0];
}

/** Validate a phone value object — returns error string or "" */
export function validatePhone(val, required = true) {
  const country = getCountryByCode(val?.countryCode || "+91");
  const num = (val?.number || "").trim();
  if (!num) return required ? "Phone number is required" : "";
  if (!/^\d+$/.test(num)) return "Only digits allowed";
  if (num.length !== country.digits)
    return `Must be exactly ${country.digits} digits for ${country.name}`;
  return "";
}

/** Format a phone value for display / submission */
export function formatPhone(val) {
  if (!val) return "";
  return `${val.countryCode} ${val.number}`;
}

/* ── Component ── */
export default function PhoneInput({ value, onChange, error, label, required, placeholder, inputClassName }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropRef = useRef(null);

  const country = getCountryByCode(value?.countryCode || "+91");
  const filtered = COUNTRIES.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.code.includes(search)
  );

  // Close dropdown on outside click
  useEffect(() => {
    function handle(e) {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const selectCountry = (c) => {
    onChange({ countryCode: c.code, number: "" });
    setOpen(false);
    setSearch("");
  };

  const handleNumber = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, country.digits);
    onChange({ countryCode: country.code, number: raw });
  };

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <div className={`flex border rounded-xl overflow-visible focus-within:ring-2 transition ${
        error ? "border-red-400 focus-within:ring-red-400" : "border-gray-200 focus-within:ring-green-400"
      }`}>
        {/* Country selector */}
        <div className="relative" ref={dropRef}>
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            className="flex items-center gap-1.5 px-3 py-2.5 bg-gray-50 border-r border-gray-200 text-sm text-gray-700 hover:bg-gray-100 transition rounded-l-xl whitespace-nowrap h-full"
          >
            <span className="text-base">{country.flag}</span>
            <span className="font-medium">{country.code}</span>
            <span className="text-gray-400 text-xs">▾</span>
          </button>

          {open && (
            <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl w-56 max-h-60 overflow-hidden flex flex-col">
              <div className="p-2 border-b border-gray-100">
                <input
                  type="text"
                  autoFocus
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search country…"
                  className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-green-400"
                />
              </div>
              <div className="overflow-y-auto flex-1">
                {filtered.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-3">No results</p>
                )}
                {filtered.map(c => (
                  <button
                    key={c.code + c.name}
                    type="button"
                    onClick={() => selectCountry(c)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-green-50 text-left transition ${
                      c.code === country.code ? "bg-green-50 text-green-700 font-medium" : "text-gray-700"
                    }`}
                  >
                    <span className="text-sm">{c.flag}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="text-gray-400">{c.code}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Number input */}
        <input
          type="tel"
          inputMode="numeric"
          value={value?.number || ""}
          onChange={handleNumber}
          maxLength={country.digits}
          placeholder={placeholder || `${country.digits}-digit number`}
          className={`flex-1 px-3 py-2.5 text-sm focus:outline-none bg-white rounded-r-xl ${inputClassName || ""}`}
        />
      </div>

      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      {!error && (
        <p className="text-xs text-gray-400 mt-0.5">
          {country.flag} {country.name} — {country.digits} digit{country.digits !== 1 ? "s" : ""}
        </p>
      )}
    </div>
  );
}
